import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "../auth/useAuth";
import type { Plan, Subscription, SystemFeatureCode } from "./models";
import { subscriptionService } from "./services/subscription.service";
import type { SubscriptionOverview } from "./types";

export interface SubscriptionContextValue {
  subscription: Subscription | null;
  plan: Plan | null;
  overview: SubscriptionOverview | null;
  enabledFeatures: SystemFeatureCode[] | undefined;
  daysRemaining: number | null;
  isTrial: boolean;
  isExpired: boolean;
  isLoading: boolean;
  hasFeature: (code: SystemFeatureCode | string) => boolean;
  refresh: () => Promise<void>;
}

export const SubscriptionContext =
  createContext<SubscriptionContextValue | null>(null);

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({
  children,
}) => {
  const { tenant, isAuthenticated } = useAuth();
  const [overview, setOverview] = useState<SubscriptionOverview | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!tenant?.id || !isAuthenticated) {
      setOverview(null);
      return;
    }
    setIsLoading(true);
    try {
      const data = await subscriptionService.getTenantSubscription(tenant.id);
      setOverview(data);
    } catch {
      setOverview(null);
    } finally {
      setIsLoading(false);
    }
  }, [tenant?.id, isAuthenticated]);

  useEffect(() => {
    load();
  }, [load]);

  const hasFeature = useCallback(
    (code: SystemFeatureCode | string) => {
      if (!overview) return true;
      return overview.subscription.features.includes(code as SystemFeatureCode);
    },
    [overview]
  );

  const value = useMemo<SubscriptionContextValue>(
    () => ({
      subscription: overview?.subscription ?? null,
      plan: overview?.plan ?? null,
      overview,
      enabledFeatures: overview?.subscription.features,
      daysRemaining: overview?.daysRemaining ?? null,
      isTrial: overview?.isTrial ?? false,
      isExpired: overview?.isExpired ?? false,
      isLoading,
      hasFeature,
      refresh: load,
    }),
    [overview, isLoading, hasFeature, load]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
